import { useQueryClient } from 'react-query'
import type { Dispatch, SetStateAction } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import OwnerAssignmentDropdown from 'components/owner-assignment-dropdown'
import useApiHelpers from 'lib/effects/api-helpers'
import { Lead } from 'lib/models/lead'
import { LEAD_EVENT_TYPES } from 'utils/enums'

const StyledButton = styled.button`
  padding: 0;
  text-decoration: underline;
  text-align: left;

  &:hover {
    text-decoration: none;
  }
`

const OwnerAssignment = ({
  lead,
  loggedInAdminUserId,
  setEnabled,
}: {
  lead: Lead
  loggedInAdminUserId: number
  setEnabled: Dispatch<SetStateAction<boolean>>
}) => {
  const {
    owner,
    conversionScore,
    status,
    scheduledNextCallAtTimeSet,
    scheduledNextCallAt,
    unsuitableReason,
    blockedReason,
    updatedAt,
  } = lead.attributes
  const showAssignSelfButton = owner?.id !== loggedInAdminUserId
  const cache = useQueryClient()

  const { createItem: createLeadEvent, isCreating: isCreatingLeadEvent } =
    useApiHelpers({
      baseUrl: `/api/lead-events`,
      type: 'lead_events',
    })

  const handleAssignOwner = async (ownedByAdminUserId: number | null) => {
    await createLeadEvent({
      leadId: lead.id,
      type: LEAD_EVENT_TYPES.OWNER_CHANGED,
      metadata: {},
      leadChanges: {
        status,
        scheduledNextCallAtTimeSet,
        scheduledNextCallAt,
        unsuitableReason,
        conversionScore,
        blockedReason,
        ownedByAdminUserId,
      },
    })
    cache.invalidateQueries('leads')
    setEnabled(true)
    toast('Lead assigned.', {
      toastId: 'lead-assigned',
    })
  }

  return (
    <OwnerAssignmentDropdown
      updatedAt={updatedAt}
      trigger={<StyledButton>{owner ? owner.name : 'Unassigned'}</StyledButton>}
      loggedInAdminUserId={loggedInAdminUserId}
      initialAdminUserId={owner?.id || null}
      isSubmitting={isCreatingLeadEvent}
      canAssign={(adminUserId) => owner?.id !== adminUserId}
      showAssignSelfButton={showAssignSelfButton}
      onToggle={() => setEnabled((prevEnabled: boolean) => !prevEnabled)}
      onAssign={handleAssignOwner}
    />
  )
}

export default OwnerAssignment
